// Spaceship GLB loader + trimesh collider extractor for the
// "Spaceship in Saturn's Rings" map.
//
// Both the physics Arena (in the sim layer) and the visual ArenaView (in the
// render layer) need the same scaled ship: the visual wants the GLB mesh
// group, and the physics wants a Rapier trimesh that matches the actual
// hull geometry. `preloadSpaceshipAssets()` runs once up front to load,
// scale, centre and bake the collider data; `getSpaceshipAssets()` is the
// synchronous accessor thereafter.

import * as THREE from 'three';
import { createGLTFLoader } from './glbLoader.js';
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { preloadShipCollision } from '../maps/bundles/spaceship-in-rings/shipCollision';

const MODEL_PATH = '/models/world/spaceship.glb';

// Target world-space size of the ship's longest bbox dimension — about 20×
// the player's pelvis radius (0.22m). Yields a wingspan around 24m, which
// is big enough to read as "I'm on a capital ship" without making the
// player sprite vanish against the hull.
const SHIP_TARGET_MAX_DIM = 24.0;
// GLB comes in rotated 90° off the expected heading. -90° on Y points the
// nose along -Z (toward the camera in the default map framing).
const SHIP_YAW_OFFSET_DEG = -90;

export interface ShipCollisionMesh {
  /** Flat xyz array of world-space vertex positions (after scale/centre).
   *  Passed directly to `ColliderDesc.trimesh`. */
  vertices: Float32Array;
  /** Triangle indices into `vertices`. 3 per triangle. */
  indices: Uint32Array;
}

export interface SpaceshipAssets {
  /** Scaled, centred ship mesh group. Cloned freely by consumers (preview
   *  screen + in-game view) so each can reparent without affecting others. */
  modelTemplate: THREE.Group;
  collision: ShipCollisionMesh;
  /** World-space size of the ship's longest axis (for preview camera framing). */
  maxDim: number;
  /** World-space bbox of the scaled model (useful for dev diagnostics). */
  bbox: THREE.Box3;
  /** Total triangle count summed across every visible mesh in the GLB. */
  modelTriCount: number;
  /** Collider tri count. Same shape as the ship — trimesh mirrors the mesh. */
  colliderTriCount: number;
}

let _loadPromise: Promise<SpaceshipAssets> | null = null;
let _loaded: SpaceshipAssets | null = null;

/** Kick off the GLB load + collider bake. Idempotent — subsequent calls
 *  return the same Promise. Await in `main.ts` before building the Renderer
 *  so the ship is available synchronously from that point on. */
export function preloadSpaceshipAssets(): Promise<SpaceshipAssets> {
  if (_loadPromise) return _loadPromise;
  _loadPromise = loadImpl();
  return _loadPromise;
}

/** Synchronous accessor. Returns null if preload hasn't finished. */
export function getSpaceshipAssets(): SpaceshipAssets | null {
  return _loaded;
}

async function loadImpl(): Promise<SpaceshipAssets> {
  // Fire the collision-JSON fetch in parallel with the GLB load. The sim
  // layer (both server and multiplayer client) consumes the JSON directly
  // — no THREE required — so by priming it here we guarantee one awaited
  // preloadSpaceshipAssets() leaves both layers ready.
  const collisionPromise = preloadShipCollision();
  const raw = await new Promise<THREE.Group>((resolve, reject) => {
    createGLTFLoader().load(
      MODEL_PATH,
      (gltf) => resolve(gltf.scene),
      undefined,
      (err) => reject(err)
    );
  });

  // Apply the yaw offset to the raw GLB up-front. With a trimesh collider
  // derived from the same geometry, visual and collider are extracted
  // together — rotating once here keeps them aligned.
  raw.rotation.y = SHIP_YAW_OFFSET_DEG * Math.PI / 180;

  // Scale so the longest bbox axis matches the target. Works regardless of
  // what units the modelling tool exported in.
  raw.updateMatrixWorld(true);
  const rawBbox = new THREE.Box3().setFromObject(raw);
  const rawSize = rawBbox.getSize(new THREE.Vector3());
  const rawMax = Math.max(rawSize.x, rawSize.y, rawSize.z);
  const scale = rawMax > 1e-6 ? SHIP_TARGET_MAX_DIM / rawMax : 1;

  const template = new THREE.Group();
  template.add(raw);
  template.scale.setScalar(scale);
  // After scale, centre XZ on origin and drop so the top of the ship sits
  // at y ≈ 0. Players spawn at their usual y ≈ 0.5 just above that.
  template.updateMatrixWorld(true);
  const scaledBbox = new THREE.Box3().setFromObject(template);
  const scaledCentre = scaledBbox.getCenter(new THREE.Vector3());
  const scaledTopY = scaledBbox.max.y;
  template.position.x -= scaledCentre.x;
  template.position.z -= scaledCentre.z;
  template.position.y -= scaledTopY;
  template.updateMatrixWorld(true);

  // Neutralise materials — without an envMap the raw PBR materials can
  // render near-black; also lets the scene's IBL + key light carry the bulk
  // of the shading. Same traversal flags every mesh as a shadow caster +
  // receiver; clones inherit the flag, so both the preview and in-game
  // ship instance gain shadows for free.
  template.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!(mesh as { isMesh?: boolean }).isMesh) return;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const mat of mats) {
      const std = mat as THREE.MeshStandardMaterial;
      if ('metalness' in std) std.metalness = Math.min(Math.max(std.metalness ?? 0, 0.20), 0.42);
      if ('roughness' in std) std.roughness = Math.max(std.roughness ?? 1, 0.65);
    }
  });

  const bbox = new THREE.Box3().setFromObject(template);
  const size = bbox.getSize(new THREE.Vector3());

  // Ensure the JSON-baked collision is loaded (it was kicked off at the top
  // of loadImpl). We use bakeTrimesh as a fallback so if the committed JSON
  // is ever out of date relative to the GLB, single-player still has a
  // matching collider — but the cached JSON wins when available so client
  // and server agree vertex-for-vertex.
  await collisionPromise;
  const collision = bakeTrimesh(template);

  _loaded = {
    modelTemplate: template,
    collision,
    maxDim: Math.max(size.x, size.y, size.z),
    bbox,
    modelTriCount: Math.round(collision.indices.length / 3),
    colliderTriCount: Math.round(collision.indices.length / 3)
  };
  return _loaded;
}

/** Walk the template's scene graph and merge every visible mesh's geometry
 *  into a single (vertices, indices) pair in world space. The result goes
 *  straight into `ColliderDesc.trimesh`, giving the ship a collision surface
 *  that matches the rendered hull exactly. */
function bakeTrimesh(group: THREE.Group): ShipCollisionMesh {
  const vertexList: number[] = [];
  const indexList: number[] = [];
  const tmp = new THREE.Vector3();

  group.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!(mesh as { isMesh?: boolean }).isMesh) return;

    let geom = mesh.geometry as THREE.BufferGeometry;
    // Non-indexed geometries have one vertex per triangle corner — fine for
    // trimesh indexing but redundant. `mergeVertices` collapses coincident
    // positions, which shrinks the vertex buffer and is what Rapier's BVH
    // wants anyway.
    if (!geom.index) geom = mergeVertices(geom);

    const posAttr = geom.getAttribute('position') as THREE.BufferAttribute | undefined;
    const idx = geom.index;
    if (!posAttr || !idx) return;

    const baseVertex = vertexList.length / 3;
    const matrix = mesh.matrixWorld;
    for (let i = 0; i < posAttr.count; i++) {
      tmp.fromBufferAttribute(posAttr, i).applyMatrix4(matrix);
      vertexList.push(tmp.x, tmp.y, tmp.z);
    }
    for (let i = 0; i < idx.count; i++) {
      indexList.push(idx.getX(i) + baseVertex);
    }
  });

  return {
    vertices: new Float32Array(vertexList),
    indices: new Uint32Array(indexList)
  };
}
